/*
 * CONFIDENTIAL AND PROPRIETARY
 *
 * Copyright (c) 2003 - 2021 Levels Beyond
 * Copyright (c) 2021 - 2023 Signiant (by acquisition)
 * Portions Copyright (c) 2023 - 2026 Quickplay Media
 *
 * ALL RIGHTS RESERVED
 *
 * This software is licensed to Quickplay Media under a formal
 * license agreement. Unauthorized use, reproduction, or
 * distribution is strictly prohibited.
 */

'use strict';

angular.module('prism-ui.queues').service('QueuesViewService', function (
    _,
    $q,
    DashboardResourceFactory,
    ModalService,
    PackageResourceFactory,
    PaginationService,
    PartnerResourceFactory,
    PermissionsService,
    QueuesResourceFactory
) {

    var self = this;

    var colorMap = {
        'Aspera': '#c647f4',
        'FTP': '#f7a261',
        'Network': '#8ae3c0',
        'upLynk': '#7cb7ea',
        'Stalled': '#bd000b',
        'SFTP': '#bababe',
        'iTunes': '#7f89e6',
        'Amazon S3': '#eee779',
        'Executing': '#2ec92d',
        'Queued': '#f68f41',
        'Pending Restore': '#f7a261',
        'Pending Delivery': '#bababe',
        'Stalls': '#bd000b',
        'Pending Approval': '#7f89e6',
        'Paused': '#eee779',
        'Waiting': '#c647f4',
        'Completed': '#9933ff',
    };

    var errorStates = {'Stalled': true};
    var ignoreForGraphStates = {'Completed': true};
    var ignoreForPanelStates = {'Waiting': true};

    var statusToLabel = function (status) {
        switch (status) {
            case 'Completed':
                return 'Finished';
            default:
                return status;
        }
    };

    var hasBandwidthWarningFlag = function (deliveryReportQueues, queueName) {
        for (var i = 0; i < deliveryReportQueues.length; i++) {
            if (queueName === deliveryReportQueues[i].name) {
                return deliveryReportQueues[i].bandwidthWarningFlag;
            }
        }

        return false;
    };

    var PREVIEW_TAB_VIEW_STATE = 'PACKAGE_VIDEO_PREVIEW_VIEW_STATE';
    var DASHBOARD_QUEUE_VIEW_STATE = 'DASHBOARD_QUEUE_VIEW';

    self.getQueueViewPermissions = function () {
        return $q.all({
            preview: PermissionsService.canRead(PREVIEW_TAB_VIEW_STATE),
            read: PermissionsService.canRead(DASHBOARD_QUEUE_VIEW_STATE),
            write: PermissionsService.canWrite(DASHBOARD_QUEUE_VIEW_STATE)
        });
    };

    self.queueId = null;
    self.showFinishedDataInGraph = false;

    self.parseDashboardData = function (dashboardData, parseAsFloat, bandwidth, bandwidthReport) {
        var parsedDashboardData = {
            header: dashboardData.header,
            subHeader: dashboardData.subHeader,
            total: (parseAsFloat) ? parseFloat(dashboardData.total).toFixed(2) : parseInt(dashboardData.total),
            unit: dashboardData.unit,
            data: [],
            series: [],
            hasError: false,
            hasWarning: false
        };

        var includeSubSeries = 'subSeries' in dashboardData;
        var hideCompletedForPills = false;

        if (dashboardData.lastStallDate) {
            parsedDashboardData.lastStallDate = dashboardData.lastStallDate;
        }

        for (var dataKey in dashboardData.data) {
            if (!ignoreForPanelStates[dataKey]) {
                var dataPoint = {field: dataKey, value: dashboardData.data[dataKey]};
                var dataValueString = (parseAsFloat) ? parseFloat(dataPoint.value).toFixed(2) : parseInt(dataPoint.value);
                var legendName = statusToLabel(dataKey) + ((dataPoint.value) ? ' (' + dataValueString + ')' : '');
                var seriesEntry = {field: dataKey, name: legendName, color: colorMap[dataKey]};
                if (errorStates[dataPoint.field] && parseInt(dataPoint.value) > 0) {
                    parsedDashboardData.hasError = true;
                }
                if (ignoreForGraphStates[dataPoint.field] && !self.showFinishedDataInGraph) {
                    seriesEntry.ignoreForGraph = hideCompletedForPills = true;
                    parsedDashboardData.total -= dataPoint.value;
                }
                parsedDashboardData.data.push(dataPoint);
                parsedDashboardData.series.push(seriesEntry);
            }
        }

        if (includeSubSeries) {
            parsedDashboardData.subSeries = [];

            for (var i = 0; i < dashboardData.subSeries.length; i++) {
                var queueData = dashboardData.subSeries[i];
                var totalBandwidth = 0;

                if (bandwidthReport && bandwidthReport.totalBandwidth > 0) {
                    for (var k = 0; k < bandwidthReport.deliveryQueues.length; k++) {
                        if (queueData.id === bandwidthReport.deliveryQueues[k].id) {
                            totalBandwidth = _.round(parseInt(bandwidthReport.deliveryQueues[k].bandwidth) / 1000000, 3);
                        }
                    }
                }

                var parsedSubSeriesDashboardData = {
                    header: queueData.header,
                    id: queueData.id,
                    total: parseInt(queueData.total),
                    totalBandwidth: totalBandwidth,
                    unit: (bandwidth) ? bandwidth.unit : ' Mbps',
                    bandwidthWarningFlag: (dashboardData.bandwidthReport) ? hasBandwidthWarningFlag(dashboardData.bandwidthReport.deliveryQueues, queueData.header) : false,
                    showFinished: self.showFinishedDataInGraph,
                    data: [],
                    hasError: false,
                    hasWarning: false
                };

                for (var seriesDataKey in queueData.data) {
                    if (!ignoreForPanelStates[seriesDataKey]) {
                        var statusValue = parseInt(queueData.data[seriesDataKey]);
                        var subSeriesDataPoint = {
                            field: seriesDataKey,
                            value: (statusValue > 0) ? statusValue : 0,
                            color: colorMap[seriesDataKey]
                        };
                        if (errorStates[subSeriesDataPoint.field] && parseInt(subSeriesDataPoint.value) > 0) {
                            subSeriesDataPoint.hasError = true;
                        }
                        if (ignoreForGraphStates[subSeriesDataPoint.field] && !self.showFinishedDataInGraph) {
                            subSeriesDataPoint.total -= subSeriesDataPoint.value;
                        } else {
                            parsedSubSeriesDashboardData.data.push(subSeriesDataPoint);
                        }
                    }
                }

                parsedDashboardData.subSeries.push(parsedSubSeriesDashboardData);
            }
        }

        return parsedDashboardData;
    };

    self.saveQueueLimiters = function (queueObj) {
        QueuesResourceFactory.updateQueue({id: queueObj.id}, queueObj);
    };

    self.deleteQueueLimiter = function (limiterObj) {
        return QueuesResourceFactory.deleteQueueLimiter({id: limiterObj.id}).$promise;
    };

    self.saveQueueLimiter = function (limiterObj) {
        return QueuesResourceFactory.createNewQueueLimiter({id: self.selectedQueueId}, limiterObj).$promise;
    };

    self.getQueuesListData = function (queueId) {
        self.selectedQueueId = queueId;
        var queue = QueuesResourceFactory.get({id: queueId}).$promise;
        var queueLimiters = QueuesResourceFactory.getAllQueueLimiters({id: queueId}).$promise;
        return $q.all({queueData: queue, queueLimiters: queueLimiters}).then(function (data) {
            return data;
        });
    };

    self.getLimiterEditData = function (limiterId) {
        var promises = {
            partners: PartnerResourceFactory.query({fetchLimit: -1}).$promise,
            categoryTypes: PackageResourceFactory.getCategories().$promise
        };
        if (limiterId > -1) {
            promises.limiter = QueuesResourceFactory.getSingleQueueLimiter({id: limiterId}).$promise;
        }
        return $q.all(promises);
    };

    self.getTranscodesPanelData = function (includeSubSeries) {
        return DashboardResourceFactory.getTranscodeSteps({
            'showCompleted': self.showFinishedDataInGraph,
            'includeSubSeries': includeSubSeries
        }).$promise.then(function (dashboardData) {
            return self.parseDashboardData(dashboardData);
        });
    };

    self.getDeliveriesPanelData = function (includeSubSeries) {
        var steps = DashboardResourceFactory.getDeliverySteps({
            'showCompleted': self.showFinishedDataInGraph,
            'includeSubSeries': includeSubSeries
        }).$promise;
        var bandwidth = DashboardResourceFactory.getDeliveryBandwidth().$promise;
        var bandwidthReport = DashboardResourceFactory.getDeliveryBandwidthReport().$promise;
        return $q.all({
            steps: steps,
            bandwidth: bandwidth,
            bandwidthReport: bandwidthReport
        }).then(function (dashboardData) {
            var parsedData = self.parseDashboardData(dashboardData.steps, false, dashboardData.bandWidth, dashboardData.bandwidthReport);
            parsedData.subHeader = 'TOTAL BANDWIDTH: ' + dashboardData.bandwidth.total + ' ' + dashboardData.bandwidth.unit;
            return parsedData;
        });
    };

    self.getRestoresPanelData = function (includeSubSeries) {
        return DashboardResourceFactory.getRestoreSteps({
            'showCompleted': self.showFinishedDataInGraph,
            'includeSubSeries': includeSubSeries
        }).$promise.then(function (dashboardData) {
            return self.parseDashboardData(dashboardData);
        });
    };

    self.getFileCopyPanelData = function (includeSubSeries) {
        return DashboardResourceFactory.getFileCopySteps({
            'showCompleted': self.showFinishedDataInGraph,
            'includeSubSeries': includeSubSeries
        }).$promise.then(function (dashboardData) {
            return self.parseDashboardData(dashboardData);
        });
    };

    self.getQueueStepData = function (queryOpts) {
        var queueDataPromise = QueuesResourceFactory.get({id: queryOpts.id}).$promise;
        var stepDataPromise = QueuesResourceFactory.getQueueSteps(queryOpts, function (data, headers) {
            PaginationService.parseResponseHeaders(headers());
        }).$promise;

        return $q.all({queueData: queueDataPromise, stepData: stepDataPromise});
    };

    self.saveQueueStep = function (queue) {
        return QueuesResourceFactory.updateQueueStepPriority({id: queue.stepId, priority: queue.priority}).$promise;
    };

    self.loadSummaryData = function () {
        var dataPromises = {};
        dataPromises.transcodes = self.getTranscodesPanelData();
        dataPromises.deliveries = self.getDeliveriesPanelData();
        dataPromises.restores = self.getRestoresPanelData();
        dataPromises.fileCopy = self.getFileCopyPanelData();

        return $q.all(dataPromises);
    };

    self.loadGroupSummaryData = function (groupName) {
        switch (groupName) {
            case 'transcodes':
                return $q.when(self.getTranscodesPanelData(true));
            case 'deliveries':
                return $q.when(self.getDeliveriesPanelData(true));
            case 'restores':
                return $q.when(self.getRestoresPanelData(true));
            case 'fileCopy':
                return $q.when(self.getFileCopyPanelData(true));
        }
    };
    self.clearQueue = (queueId) => {
        return QueuesResourceFactory.clearQueue({id: queueId}).$promise;
    };
});
